import axios from '@/services/axiosConfig';
import { format } from 'date-fns';
const STEPS = {
    UPLOAD: 'upload',
    MAPPING: 'mapping',
    PREVIEW: 'preview',
    CONFIRM: 'confirm',
};

const state = {
    isModalOpen: false,
    currentStep: STEPS.UPLOAD,

    uploadedFiles: [],

    matchedTrips: [],
    selectedTripIds: [],
    matchingInProgress: false,

    fetchedShifts: [],
    fetchingShifts: false,

    fetchedTrips: [],
    fetchingTrips: false,

    importInProgress: false,
    importResult: null,
    importProgress: { updated: 0, failed: 0, total: 0 },
};

const mutations = {
    SET_MODAL_OPEN(state, isOpen) {
        state.isModalOpen = isOpen;
    },

    SET_CURRENT_STEP(state, step) {
        state.currentStep = step;
    },

    ADD_UPLOADED_FILE(state, fileData) {
        state.uploadedFiles = [...state.uploadedFiles, fileData];
    },

    REMOVE_UPLOADED_FILE(state, fileId) {
        state.uploadedFiles = state.uploadedFiles.filter((f) => f.id !== fileId);
    },

    UPDATE_UPLOADED_FILE(state, { fileId, updates }) {
        const index = state.uploadedFiles.findIndex((f) => f.id === fileId);
        if (index !== -1) {
            state.uploadedFiles = [
                ...state.uploadedFiles.slice(0, index),
                { ...state.uploadedFiles[index], ...updates },
                ...state.uploadedFiles.slice(index + 1),
            ];
        }
    },

    CLEAR_UPLOADED_FILES(state) {
        state.uploadedFiles = [];
    },

    SET_MATCHED_TRIPS(state, trips) {
        state.matchedTrips = trips;
        state.selectedTripIds = trips
            .filter((t) => t.matchResult && t.matchResult.confidence >= 95)
            .map((t) => t.externalId);
    },

    TOGGLE_TRIP_SELECTION(state, tripId) {
        const index = state.selectedTripIds.indexOf(tripId);
        if (index === -1) {
            state.selectedTripIds = [...state.selectedTripIds, tripId];
        } else {
            state.selectedTripIds = state.selectedTripIds.filter((id) => id !== tripId);
        }
    },

    SELECT_ALL_TRIPS(state) {
        state.selectedTripIds = state.matchedTrips.map((t) => t.externalId);
    },

    DESELECT_ALL_TRIPS(state) {
        state.selectedTripIds = [];
    },

    SELECT_ZERO_DIFF_TRIPS(state) {
        state.selectedTripIds = state.matchedTrips
            .filter((t) => {
                if (!t.matchResult || t.matchResult.confidence === 0) return false;
                const externalAmount = t.totalAmount || 0;
                const internalAmount = t.matchResult.tripData?.totalAmount || 0;
                return internalAmount === externalAmount;
            })
            .map((t) => t.externalId);
    },

    SELECT_ABOVE_THRESHOLD_TRIPS(state, threshold) {
        state.selectedTripIds = state.matchedTrips
            .filter((t) => t.matchResult && t.matchResult.confidence >= threshold)
            .map((t) => t.externalId);
    },

    UPDATE_TRIP_MATCH(state, { externalId, matchResult }) {
        const tripIndex = state.matchedTrips.findIndex((t) => t.externalId === externalId);
        if (tripIndex !== -1) {
            const updatedTrip = {
                ...state.matchedTrips[tripIndex],
                matchResult,
                isManualMatch: true,
            };
            state.matchedTrips = [
                ...state.matchedTrips.slice(0, tripIndex),
                updatedTrip,
                ...state.matchedTrips.slice(tripIndex + 1),
            ];
            if (
                matchResult &&
                matchResult.confidence > 0 &&
                !state.selectedTripIds.includes(externalId)
            ) {
                state.selectedTripIds = [...state.selectedTripIds, externalId];
            }
        }
    },

    SET_MATCHING_IN_PROGRESS(state, inProgress) {
        state.matchingInProgress = inProgress;
    },

    SET_FETCHED_SHIFTS(state, shifts) {
        state.fetchedShifts = shifts;
    },

    SET_FETCHING_SHIFTS(state, fetching) {
        state.fetchingShifts = fetching;
    },

    SET_FETCHED_TRIPS(state, trips) {
        state.fetchedTrips = trips;
    },

    SET_FETCHING_TRIPS(state, fetching) {
        state.fetchingTrips = fetching;
    },

    SET_IMPORT_IN_PROGRESS(state, inProgress) {
        state.importInProgress = inProgress;
    },

    SET_IMPORT_RESULT(state, result) {
        state.importResult = result;
    },

    SET_IMPORT_PROGRESS(state, progress) {
        state.importProgress = progress;
    },

    RESET_STATE(state) {
        state.currentStep = STEPS.UPLOAD;
        state.uploadedFiles = [];
        state.matchedTrips = [];
        state.selectedTripIds = [];
        state.matchingInProgress = false;
        state.fetchedShifts = [];
        state.fetchingShifts = false;
        state.fetchedTrips = [];
        state.fetchingTrips = false;
        state.importInProgress = false;
        state.importResult = null;
        state.importProgress = { updated: 0, failed: 0, total: 0 };
    },
};

const actions = {
    openModal({ commit }) {
        commit('SET_MODAL_OPEN', true);
    },

    closeModal({ commit }) {
        commit('SET_MODAL_OPEN', false);
        setTimeout(() => {
            commit('RESET_STATE');
        }, 300);
    },

    addFile({ commit }, { file, platform, parsedTrips, error = null }) {
        const fileData = {
            id: `${file.name}-${Date.now()}`,
            file,
            platform,
            parsedTrips: parsedTrips || [],
            error,
        };
        commit('ADD_UPLOADED_FILE', fileData);
        return fileData.id;
    },

    removeFile({ commit }, fileId) {
        commit('REMOVE_UPLOADED_FILE', fileId);
    },

    updateFile({ commit }, { fileId, updates }) {
        commit('UPDATE_UPLOADED_FILE', { fileId, updates });
    },

    clearFiles({ commit }) {
        commit('CLEAR_UPLOADED_FILES');
    },

    nextStep({ commit, state }) {
        const stepOrder = [STEPS.UPLOAD, STEPS.MAPPING, STEPS.PREVIEW, STEPS.CONFIRM];
        const currentIndex = stepOrder.indexOf(state.currentStep);
        if (currentIndex < stepOrder.length - 1) {
            commit('SET_CURRENT_STEP', stepOrder[currentIndex + 1]);
        }
    },

    prevStep({ commit, state }) {
        const stepOrder = [STEPS.UPLOAD, STEPS.MAPPING, STEPS.PREVIEW, STEPS.CONFIRM];
        const currentIndex = stepOrder.indexOf(state.currentStep);
        if (currentIndex > 0) {
            commit('SET_CURRENT_STEP', stepOrder[currentIndex - 1]);
        }
    },

    goToStep({ commit }, step) {
        commit('SET_CURRENT_STEP', step);
    },

    setMatchedTrips({ commit }, trips) {
        commit('SET_MATCHED_TRIPS', trips);
    },

    toggleTripSelection({ commit }, tripId) {
        commit('TOGGLE_TRIP_SELECTION', tripId);
    },

    selectAllTrips({ commit }) {
        commit('SELECT_ALL_TRIPS');
    },

    deselectAllTrips({ commit }) {
        commit('DESELECT_ALL_TRIPS');
    },

    selectZeroDiffTrips({ commit }) {
        commit('SELECT_ZERO_DIFF_TRIPS');
    },

    selectAboveThresholdTrips({ commit }, threshold) {
        commit('SELECT_ABOVE_THRESHOLD_TRIPS', threshold);
    },

    updateTripMatch({ commit }, { externalId, matchResult }) {
        commit('UPDATE_TRIP_MATCH', { externalId, matchResult });
    },

    async fetchShiftsByDateRange({ commit }, { startAt, endAt }) {
        commit('SET_FETCHING_SHIFTS', true);
        commit('SET_FETCHED_SHIFTS', []);

        try {
            const queryData = {
                rangeStartAt: startAt,
                rangeEndAt: endAt,
                driverNumber: null,
                licenseNumber: null,
            };

            const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/shifts`;
            const response = await axios.post(url, queryData, {
                withCredentials: true,
            });

            commit('SET_FETCHED_SHIFTS', response.data || []);
            return response.data || [];
        } catch (error) {
            console.error('Error fetching shifts by date range:', error);
            commit('SET_FETCHED_SHIFTS', []);
            throw error;
        } finally {
            commit('SET_FETCHING_SHIFTS', false);
        }
    },

    async fetchTripsByDateRange({ commit }, { startAt, endAt }) {
        commit('SET_FETCHING_TRIPS', true);
        commit('SET_FETCHED_TRIPS', []);

        try {
            const queryData = {
                rangeStartAt: startAt,
                rangeEndAt: endAt,
                driverNumber: null,
                licenseNumber: null,
            };

            const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/trips`;
            const response = await axios.post(url, queryData, {
                withCredentials: true,
            });

            commit('SET_FETCHED_TRIPS', response.data || []);
            return response.data || [];
        } catch (error) {
            console.error('Error fetching trips by date range:', error);
            commit('SET_FETCHED_TRIPS', []);
            throw error;
        } finally {
            commit('SET_FETCHING_TRIPS', false);
        }
    },

    async executeImport({ commit, state }, overwriteOptions = {}) {
        const BATCH_SIZE = 20;

        commit('SET_IMPORT_IN_PROGRESS', true);
        commit('SET_IMPORT_PROGRESS', { updated: 0, failed: 0, total: 0 });

        try {
            const tripsToUpdate = state.matchedTrips.filter(
                (t) =>
                    state.selectedTripIds.includes(t.externalId) && t.matchResult?.matchedTripUuid,
            );

            const skipped = state.matchedTrips.filter(
                (t) =>
                    state.selectedTripIds.includes(t.externalId) && !t.matchResult?.matchedTripUuid,
            ).length;

            let updated = 0;
            let failed = 0;
            const total = tripsToUpdate.length;

            commit('SET_IMPORT_PROGRESS', { updated, failed, total });

            for (let i = 0; i < tripsToUpdate.length; i += BATCH_SIZE) {
                const batch = tripsToUpdate.slice(i, i + BATCH_SIZE);

                for (const trip of batch) {
                    try {
                        const tripUuid = trip.matchResult.matchedTripUuid;
                        const url = `${import.meta.env.VITE_API_BASE_URL}/cpanel/trips/${tripUuid}`;

                        const updates = {};

                        if (overwriteOptions.paymentMethod) {
                            updates.paymentMethod = trip.paymentMethod;
                        }

                        if (overwriteOptions.dispatchPlatform) {
                            updates.dispatchPlatform = trip.platform;
                        }

                        if (overwriteOptions.tip && typeof trip.tip === 'number') {
                            updates.tip = trip.tip;
                        }

                        if (overwriteOptions.totalAmount && typeof trip.totalAmount === 'number') {
                            updates.paymentAmount = trip.totalAmount;
                        }

                        if (Object.keys(updates).length > 0) {
                            await axios.patch(url, updates, { withCredentials: true });
                        }

                        try {
                            const commentUrl = `${url}/comments`;
                            const today = format(new Date(), 'dd.MM.yyyy');

                            const changes = [];
                            if (updates.paymentMethod) {
                                changes.push(`Zahlungsmethode → ${updates.paymentMethod === 'card' ? 'Kartenzahlung' : updates.paymentMethod}`);
                            }
                            if (updates.paymentAmount !== undefined) {
                                changes.push(`Fahrpreis → ${(updates.paymentAmount / 100).toFixed(2).replace('.', ',')} €`);
                            }
                            if (updates.tip !== undefined) {
                                changes.push(`Trinkgeld → ${(updates.tip / 100).toFixed(2).replace('.', ',')} €`);
                            }
                            if (updates.dispatchPlatform) {
                                changes.push(`Plattform → ${updates.dispatchPlatform}`);
                            }

                            // Build message
                            const platformName = trip.platform === 'sumup' ? 'SumUp' :
                                trip.platform === 'izettle' ? 'iZettle' :
                                trip.platform === 'uber' ? 'Uber' :
                                trip.platform === 'bolt' ? 'Bolt' :
                                trip.platform === 'freenow' ? 'FreeNow' : trip.platform;

                            let message;
                            if (changes.length > 0) {
                                message = `${platformName}-Import am ${today}:\n${changes.join('\n')}`;
                            } else {
                                message = `${platformName}-Import am ${today}: Fahrt wurde zugeordnet.`;
                            }

                            await axios.post(commentUrl, { message }, { withCredentials: true });
                        } catch (commentError) {
                            console.error(`Failed to add comment to trip ${tripUuid}:`, commentError);
                        }

                        updated++;
                    } catch (error) {
                        console.error(
                            `Failed to update trip ${trip.matchResult.matchedTripUuid}:`,
                            error,
                        );
                        failed++;
                    }
                }

                commit('SET_IMPORT_PROGRESS', { updated, failed, total });
            }

            const result = {
                success: failed === 0,
                results: {
                    updated,
                    failed,
                    skipped,
                    total: state.selectedTripIds.length,
                },
            };

            commit('SET_IMPORT_RESULT', result);
            return result;
        } catch (error) {
            const errorResult = {
                success: false,
                error: error.response?.data?.message || 'Import fehlgeschlagen',
            };
            commit('SET_IMPORT_RESULT', errorResult);
            throw error;
        } finally {
            commit('SET_IMPORT_IN_PROGRESS', false);
        }
    },
};

const getters = {
    isModalOpen: (state) => state.isModalOpen,
    currentStep: (state) => state.currentStep,

    uploadedFiles: (state) => state.uploadedFiles,

    allParsedTrips: (state) => {
        return state.uploadedFiles.flatMap((f) =>
            (f.parsedTrips || []).map((trip) => ({
                ...trip,
                platform: f.platform,
                sourceFileId: f.id,
            })),
        );
    },

    platforms: (state) => [...new Set(state.uploadedFiles.map((f) => f.platform))],

    totalParsedTripCount: (state) =>
        state.uploadedFiles.reduce((sum, f) => sum + (f.parsedTrips?.length || 0), 0),

    hasFileErrors: (state) => state.uploadedFiles.some((f) => f.error),

    validFiles: (state) => state.uploadedFiles.filter((f) => !f.error),

    matchedTrips: (state) => state.matchedTrips,
    selectedTripIds: (state) => state.selectedTripIds,
    matchingInProgress: (state) => state.matchingInProgress,
    fetchedShifts: (state) => state.fetchedShifts,
    fetchingShifts: (state) => state.fetchingShifts,
    fetchedTrips: (state) => state.fetchedTrips,
    fetchingTrips: (state) => state.fetchingTrips,
    importInProgress: (state) => state.importInProgress,
    importResult: (state) => state.importResult,
    importProgress: (state) => state.importProgress,

    selectedTripsCount: (state) => state.selectedTripIds.length,

    matchedCount: (state) =>
        state.matchedTrips.filter((t) => t.matchResult && t.matchResult.confidence > 0).length,

    unmatchedCount: (state) =>
        state.matchedTrips.filter((t) => !t.matchResult || t.matchResult.confidence === 0).length,

    totalAmount: (state) =>
        state.matchedTrips
            .filter((t) => state.selectedTripIds.includes(t.externalId))
            .reduce((sum, t) => sum + (t.totalAmount || 0), 0),

    perPlatformStats: (state) => {
        const stats = {};
        state.matchedTrips
            .filter((t) => state.selectedTripIds.includes(t.externalId))
            .forEach((trip) => {
                const platform = trip.platform || 'unknown';
                if (!stats[platform]) {
                    stats[platform] = { count: 0, amount: 0 };
                }
                stats[platform].count += 1;
                stats[platform].amount += trip.totalAmount || 0;
            });
        return stats;
    },

    canProceedToPreview: (state) => {
        const validFiles = state.uploadedFiles.filter((f) => !f.error);
        const totalTrips = validFiles.reduce((sum, f) => sum + (f.parsedTrips?.length || 0), 0);
        return validFiles.length > 0 && totalTrips > 0;
    },

    canProceedToConfirm: (state) => state.selectedTripIds.length > 0,

    selectedTrips: (state) =>
        state.matchedTrips.filter((t) => state.selectedTripIds.includes(t.externalId)),
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
